/** Ввод даты С КЛАВИАТУРЫ, без календаря.
 *
 * На складе дату производства и срок годности переписывают с этикетки десятками
 * за приёмку, а нативный `<input type=date>` на телефоне — это модальный
 * календарь с прокруткой лет: три-четыре тапа там, где на цифровой клавиатуре
 * восемь нажатий подряд. Поэтому поле стало ТЕКСТОВЫМ (`дд.мм.гггг`), а
 * календарь остался кнопкой рядом — для тех, кому удобнее ткнуть.
 *
 * Разбор строгий и МОЛЧА НИЧЕГО НЕ ДОПРИДУМЫВАЕТ: «32.01.26» — ошибка, а не
 * 1 февраля; «13.4» — незаконченный ввод, а не 13 апреля текущего года. Дата
 * уезжает в 1С и печатается на этикетке партии, поэтому угаданная дата хуже
 * пустого поля: пустое видно, а угаданное — нет.
 */

export interface TypedDate {
  /** `yyyy-mm-dd` — то, что уходит на бэк. Пусто, если ввод неполон/неверен. */
  iso: string;
  /** Человеческая причина отказа. Пусто — либо всё верно, либо поле пустое. */
  error: string;
}

/** Диапазон годов: партии сырья живут годами, но не десятилетиями. Опечатка в
 *  годе («2062» вместо «2026») иначе доехала бы до срока годности молча. */
const YEAR_MIN = 2000;
const YEAR_MAX = 2099;

/** Цифры ввода → `дд.мм.гггг` по мере набора: точки ставит маска, человек жмёт
 *  только цифры. Разделители, набранные руками, тоже принимаем — они просто
 *  выбрасываются и ставятся заново на своих местах. */
export function maskTypedDate(raw: string): string {
  const digits = (raw || "").replace(/\D/g, "").slice(0, 8);
  const parts = [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)].filter(
    (p) => p.length > 0,
  );
  const masked = parts.join(".");
  // Точку в хвосте сохраняем: «13.» — это набранный день, курсор идёт дальше.
  return /\D$/.test(raw) && digits.length > 0 && (digits.length === 2 || digits.length === 4)
    ? `${masked}.`
    : masked;
}

/** `yyyy-mm-dd` → `дд.мм.гггг` для показа в поле (пустое остаётся пустым). */
export function isoToTyped(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || "");
  return m ? `${m[3]}.${m[2]}.${m[1]}` : "";
}

/**
 * Разбор набранного: `13.04.2026`, `13.04.26`, `13042026`, `130426`.
 *
 * Двузначный год — это 20xx: на складе других не бывает, а требовать четыре
 * цифры значит отнять ровно ту скорость, ради которой поле и сделали текстовым.
 */
export function parseTypedDate(text: string): TypedDate {
  const digits = (text || "").replace(/\D/g, "");
  if (!digits) return { iso: "", error: "" };
  if (digits.length !== 6 && digits.length !== 8) {
    return { iso: "", error: "Дата неполная — нужно дд.мм.гггг" };
  }
  const day = Number(digits.slice(0, 2));
  const month = Number(digits.slice(2, 4));
  const yearRaw = digits.slice(4);
  const year = yearRaw.length === 2 ? 2000 + Number(yearRaw) : Number(yearRaw);

  if (month < 1 || month > 12) return { iso: "", error: `Месяца ${month} не бывает` };
  if (year < YEAR_MIN || year > YEAR_MAX) {
    return { iso: "", error: `Год ${year} вне ${YEAR_MIN}–${YEAR_MAX} — проверьте ввод` };
  }
  // Проверяем календарём, а не таблицей длин месяцев: високосный февраль.
  const d = new Date(year, month - 1, day);
  if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) {
    return { iso: "", error: `Такой даты нет: ${day}.${String(month).padStart(2, "0")}.${year}` };
  }
  const p = (n: number) => String(n).padStart(2, "0");
  return { iso: `${year}-${p(month)}-${p(day)}`, error: "" };
}
